// react
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// components
import ModalsWrapper from './Modals/ModalsWrapper/ModalsWrapper';
import SelectDateCalander from './SelectDateCalander';
import DoctorAvailableSlots from './DoctorAvailableSlots';
import ButtonV1 from './ButtonVariants/ButtonV1';
import ButtonV2 from './ButtonVariants/ButtonV2';
import SliderSteps from './SliderSteps';
import Skeletonize from '../Skeletons/Skeletonize';

// images
import XIcon from '../icons/X.svg';
import ChevronUpIcon from '../icons/Chevron-Up.svg';

// utils
import * as http from '../utils/http';
import * as translator from '../utils/translator';
import * as textUtils from '../utils/Textutils';
import { useDragModal } from '../customHooks/useDragModal';
import { clientConfig } from '../config';
import { getTimeDifference } from '../utils/timestampFormatter';

// data types
import { DoctorAvailabilityData, DateAvailability } from 'data-types';
import { MainTracker } from '../PixelTrackers/MainTracker';
import { useVideoConsultationAppointment } from '../customHooks/useVideoConsultationAppointment';

// constants
import { TWENTY_FOUR_HOURS_MS } from '../utils/consts';

type props = {
	country: string;
	language: string;
    catalogID: number;
    hashID?: string;
    getEndPointURL?: string;
    postEndPointURL?: string;
    onClose?: () => void;
    onSuccess?: () => void;
    onFailure?: () => void;
    shouldAlternateBetweenTitles?: boolean;
    shouldShow: boolean;
}

const RescheduleVideoConsultationModal: React.FC<props> = ({ country="de", language="de", catalogID, hashID, getEndPointURL="v2/get-reschedule-video-consultation-data", postEndPointURL="v2/post-reschedule-video-consultation-data", onClose, onSuccess, onFailure, shouldAlternateBetweenTitles=true, shouldShow }) => {
	const {
		selectedDoctor,
		setSelectedDoctor,
		selectedDate,
		setSelectedDate,
		selectedTime,
		setSelectedTime,
        handleSelectDoctor,
        doctorsAvailabilityData,
        setDoctorsAvailabilityData,
		handleDateSelection,
        setIsSubmitting,
        allowSubmit,
	} = useVideoConsultationAppointment();
    
    // Use drag-to-close functionality
    const {
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleMouseDown,
        currentTranslateY,
        modalDragTransition,
        shouldPreventClick,
        closeModal,
    } = useDragModal({
        closeThreshold: 150,
        velocityThreshold: 1.5,
        minDragDistance: 20,
        closingAnimationDuration: 600,
        openingAnimationDuration: 800,
        onClose,
        shouldShow,
        preventBodyVerticalScroll: true,
    });

    const [isLoading, setIsLoading] = useState<boolean>(true);    
    const [flowSliderStep, setFlowSliderStep] = useState<number>(0);
    const [reFetchData, setReFetchData] = useState<boolean>(false);
    const [isDoctorsDropdownOpen, setIsDoctorsDropdownOpen] = useState<boolean>(false);
    const [oldMeetingData, setOldMeetingData] = useState<{oldDoctorName: string, oldMeetingDate: string} | null>(null);
    const [isOriginalTimeStillAvailable, setIsOriginalTimeStillAvailable] = useState<boolean>(false);
    const isMobile = window.innerWidth < 768; // md breakpoint
    const navigate = useNavigate();
    const modalContainerRef = useRef<HTMLDivElement>(null); // Modal container ref for efficient DOM access


    // handle select doctor
    const onSelectDoctor = (doctorAvailabilityData: DoctorAvailabilityData) => {
        if (flowSliderStep === 1) {
            setFlowSliderStep(0);
        }
        handleSelectDoctor(doctorAvailabilityData);
    }


    // <----------------->
    //  main logic handlers
    // <----------------->
    // Wrapper function to add modal-specific logic to the hook's handleDateSelection
    const onDateSelection = (dateString: string) => {
        handleDateSelection(dateString);
        setFlowSliderStep(1);
    };


    // main submit handler
    const handleSubmit = () => {
        if (!allowSubmit) {
            return;
        }

        setIsSubmitting(true);


        http.postToServer(
            `${postEndPointURL}/${country}/${language}`,
            {
                start: selectedTime?.start,
                end: selectedTime?.end,
                doctorID: selectedDoctor?.doctorID,
            },
            [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID]
        )
        .then((response) => {
            closeModal();
            if (onSuccess) {
                onSuccess();
                MainTracker.track('custom', 'Click to reschedule videoconsultation', {
                    'Video doctor old': oldMeetingData?.oldDoctorName,
                    'Video doctor new': selectedDoctor?.name,
                    'Video date old': oldMeetingData?.oldMeetingDate,
                    'Video date new': selectedTime?.start,
                });
            }
        })
        .catch((err) => {   
            if (err.response.data.code === 'timekit_reschedule_booking_failed') {
                if (onFailure) {
                    onFailure();
                }
            }
            else {
                window.clientLogger.error('Error submitting appointment time in reschedule video consultation',err);
                closeModal();
            }
            setReFetchData((prev) => !prev);
        })
        .finally(() => {
            setIsLoading(false);
            setIsSubmitting(false);
        });
    }

    // get data from server
	useEffect(() => {
        // do not fetch data from server if modal is closing
        if (!shouldShow) {
            return;
        }
        const pathSegments = [getEndPointURL, country, language, catalogID, ...(hashID ? [hashID] : [])];
        const formattedGetEndPointURL = pathSegments.join('/');
        // Fetch all available slots for all doctors
        http.getFromServer(
            formattedGetEndPointURL,
            [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER] 
        )
        .then((response) => {
            if (response.data && response.data.success) {
                const { doctorsAvailabilityData, oldMeetingData } = response.data as { doctorsAvailabilityData: DoctorAvailabilityData[], oldMeetingData: {oldDoctorName: string, oldMeetingDate: string, originalDoctorID: number} };
                const { originalDoctorID } = oldMeetingData;
                
                setOldMeetingData(oldMeetingData);
                
                if (originalDoctorID) {
                    // if the original doctor isn't found (became unavailable), select the first doctor from the listt
                    // Consider sorting by expertise - doctors with the same expertise as the original doctor should be first to appear
                    // const originalDoctor = doctorsAvailabilityData?.find(da => da.doctorID === originalDoctorID) ?? doctorsAvailabilityData[0];
                    const originalDoctorData = doctorsAvailabilityData?.find(da => da.doctorID === originalDoctorID);

                    if (originalDoctorData) {
                        setSelectedDoctor(originalDoctorData);
                        
                        // Try to find the original meeting time slot and date availability
                        const originalMeetingInfo = findOriginalMeetingInfo(
                            oldMeetingData.oldMeetingDate,
                            originalDoctorData.availability
                        );
                        
                        if (originalMeetingInfo) {
                            // Original time is still available - pre-select it
                            setSelectedDate(originalMeetingInfo.dateAvailability);
                            setSelectedTime(originalMeetingInfo.timeSlot);
                            setFlowSliderStep(1);
                            setIsOriginalTimeStillAvailable(true);
                        } else {
                            // Original time is no longer available - just select the first available date
                            setSelectedDate(originalDoctorData.availability[0] ?? null);
                            setIsOriginalTimeStillAvailable(false);
                        }
                    } else {
                        // Original doctor has no timeslots available - select the first doctor from the list and first available date
                        setSelectedDoctor(doctorsAvailabilityData[0]);
                        setSelectedDate(doctorsAvailabilityData[0]?.availability[0] ?? null);
                    }
                } else {
                    // THis fallback should happen only if the original doctor has been deleted/ removed from the database
                    // No original doctor ID provided by the server - set the first doctor as selected
                    setSelectedDoctor(doctorsAvailabilityData[0]);
                    setSelectedDate(doctorsAvailabilityData[0]?.availability[0] ?? null);
                }

                setDoctorsAvailabilityData(doctorsAvailabilityData);
            } else  {
                window.clientLogger.warn("RescheduleVideoConsultationModal failed, doctorsAvailabilityData is not set")
                navigate("/404");
            }
        })
        .catch((err: any) => {
            window.clientLogger.error('Error fetching availability data:', err);
            navigate("/404");
        })
        .finally(() => {
            setIsLoading(false);
        });
	}, [country, reFetchData, shouldShow]);

    if (!shouldShow) {
        return null;
    }


    return (
        <ModalsWrapper
            shouldBackgroundClickHideModal={true}
            shouldShowModal={shouldShow}
            hideModal={closeModal}
            showModal={() => {}}
            portaled={true}
        >
            <div
                id="reschedule-video-consultation-modal-container"
                className="
                    relative
                    rounded-t-[16px]
                    md:rounded-b-[16px]
                    bg-[#ffffff]
                    w-[95vw] md:w-[auto]
                    max-w-[850px]
                    self-end md:self-center
                    h-[85vh] md:h-auto max-h-[85vh]
                    overflow-hidden
                    transition-all
                    duration-300
                    ease-out
                    flex flex-col
                "
                style={{
                    transform: `translateY(${currentTranslateY}px)`,
                    transition: modalDragTransition
                }}
            >
                {/* close modal trigger */}
                <div
                    id="reschedule-video-consultation-modal-close-modal-trigger"
                    className="
                        flex md:block
                        items-center
                        relative
                        justify-center
                        py-3 md:py-0
                        md:w-[20px]
                        md:h-[20px]
                        md:absolute
                        md:top-3
                        md:right-4
                        w-full
                        cursor-pointer
                        bg-[#ffffff]
                        z-50
                        md:after:hidden
                        after:content-['']
                        after:absolute
                        after:bottom-0
                        after:left-0
                        after:w-[100%]
                        after:h-[1px]
                        after:bg-[#EDEDED]
                        touch-none
                        select-none
                        shrink-0 
                    "
                    onTouchStart={e => {isMobile && handleTouchStart(e)}}
                    onTouchMove={e => {isMobile && handleTouchMove(e)}}
                    onTouchEnd={e => {isMobile && handleTouchEnd()}}
                    onMouseDown={e => {isMobile && handleMouseDown(e)}}
                    onClick={(e) => {
                        // Prevent click from firing if we've been dragging, closing, or opening
                        if (shouldPreventClick()) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                        }
                        
                        // Only allow closing via click on desktop (where X icon is visible)
                        // On mobile, this should only be used for dragging
                        if (!isMobile && closeModal) {
                            closeModal();
                        }
                    }}
                >
                    <img 
                        src={XIcon}
                        alt="Close"
                        className="
                            hidden
                            md:block
                            w-full
                            h-full
                            brightness-0
                            invert
                        "
                    />

                    <div
                        id="mobile-close-modal-trigger"
                        className="
                            block md:hidden
                            w-[100px]
                            h-[5px]
                            rounded-[20px]
                            bg-[#EDEDED]
                        "
                    >
                    </div>
                </div>

                {/* content container */}
                <div
                    id="reschedule-video-consultation-modal-content"
                    ref={modalContainerRef}
                    className={`
                        flex flex-1
                        px-[20px] md:px-[40px]
                        md:flex 
                        bg-red-500x
                        flex-col
                        pb-7
                        md:overflow-hidden
                        custom-scrollbar
                        overflow-x-hidden
                        ${isDoctorsDropdownOpen ? 'overflow-y-hidden' : 'overflow-y-auto'}
                    `}
                    style={{
                        scrollbarGutter: 'stable'
                    }}
                >
                    {/* "RESCHEDULE" label */}
                    <Skeletonize
                        isLoading={isLoading}
                        className='w-2/3 h-[32px] md-[40px] rounded-b-[16px] mx-auto'
                        animation='pulse syncWave'
                        rounded=''
                    >
                        <div
                            id="reschedule-video-consultation-modal-label"
                            className="
                                mx-auto
                                uppercase
                                bg-[linear-gradient(88.58deg,#1F9874_1.21%,#93D530_117.29%)]
                                rounded-b-[16px]
                                text-white
                                text-[16px]
                                font-medium
                                md:py-3
                                py-2
                                px-12
                                w-fit
                                leading-[1]
                                text-center
                                animate-fade-in-up-10
                            "
                        >
                            {translator.translate(language, "default", "reschedule_video_consultation/label")}
                        </div>
                    </Skeletonize>

                    {/* titles container / header */}
                    <div
                        id="reschedule-video-consultation-modal-header"
                        className="my-5 md:my-6"
                    >
                        {/* title */}
                        <Skeletonize
                            isLoading={isLoading}
                            className='w-2/3 mx-auto'
                            animation='pulse syncWave'
                        >
                            <h1
                                id="reschedule-video-consultation-modal-header-main-title"
                                className="
                                    text-[#000000]
                                    text-[20px] md:text-[24px]
                                    font-medium
                                    text-center
                                    leading-[1.1]
                                    animate-fade-in-up-10
                                "
                            >
                                {(isOriginalTimeStillAvailable || !shouldAlternateBetweenTitles)
                                    ? translator.translate(language, "default", "reschedule_video_consultation/original_time_still_available_header_title")
                                    : translator.translate(language, "default", "reschedule_video_consultation/default_header_title")
                                }
                            </h1>
                        </Skeletonize>
                        
                        {/* subtitle */}
                        <Skeletonize
                            isLoading={isLoading}
                            className='w-1/2 mx-auto'
                            animation='pulse syncWave'
                        >
                            <h2
                                id="reschedule-video-consultation-modal-header-sub-title"
                                className="
                                    mt-3 md:mt-4
                                    font-roboto
                                    leading-[1]
                                    text-[#000000]
                                    text-[15px] md:text-[16px]
                                    font-normal
                                    text-center
                                    animate-fade-in-up-10
                                "
                            >
                                {textUtils.capitalizeFirstChar(isOriginalTimeStillAvailable
                                    ? translator.translate(language, "default", "reschedule_video_consultation/sub_title_original_time_still_available")
                                    : translator.translate(language, "default", "reschedule_video_consultation/sub_title_default")
                                )}
                            </h2>
                        </Skeletonize>
                    </div>

                    {/* doctors dropdown */}
                    <DoctorsDropdown
                        doctors={doctorsAvailabilityData}
                        selectedDoctor={selectedDoctor}
                        isDoctorsDropdownOpen={isDoctorsDropdownOpen}
                        setIsDoctorsDropdownOpen={setIsDoctorsDropdownOpen}
                        onSelectDoctor={onSelectDoctor}   
                        language={language}
                        isLoading={isLoading}
                        bottomHeightThreshold={modalContainerRef.current?.getBoundingClientRect().bottom || window.innerHeight * 0.85}
                    />

                    {/* slider progress container: step back button + slider steps dots */}
                    <div
                        id="reschedule-video-consultation-modal-slider-progress-container"
                        className="
                            md:hidden
                            pt-2
                            pb-5
                            sticky
                            top-0
                            bg-[#fffffff5]
                            z-10
                            flex
                            items-center
                            justify-between
                        "
                    >
                        <Skeletonize
                            isLoading={isLoading}
                            className='h-[13px] w-[50px]'
                            animation='pulse syncWave'
                            rounded='[80px]'
                        >
                            <ButtonV2
                                id="reschedule-video-consultation-modal-step-back-button"
                                onClick={() => {setFlowSliderStep(0)}}
                                desktopText={''}
                                mobileText={translator.translate(language, "default", "reschedule_video_consultation/back_to_calendar")}
                                className={`
                                    transition-opacity
                                    duration-1000
                                    ease-in-out
                                    md:hidden
                                    animate-fade-in-up-10
                                    ${flowSliderStep === 0 ? '!opacity-0' : ''}
                                `}
                            >
                            </ButtonV2>
                        </Skeletonize>
                    
                        <SliderSteps 
                            stepsCount={2} 
                            currentStep={flowSliderStep} 
                            additionalClassName="col-start-2 self-center md:hidden" 
                            isLoading={isLoading}
                            disableSkeleton={false}
                        />

                    </div>

                    {/* slider container - contains the calander and time slots */}
                    <div
                        id="reschedule-video-consultation-modal-calander-and-time-slots-container"
                        className="
                            flex-1
                            md:min-h-0
                            grid
                            grid-cols-[100%_100%] md:grid-cols-[1fr_1fr]
                            gap-x-[25px]
                            transition-transform duration-700 ease-in-out
                            delay-[650ms]
                            md:![transform:translate(0,0)]
                        "
                        style={{
                            transform: `translateX(calc(-${100 * flowSliderStep}% - 25px * ${flowSliderStep}))`,
                        }}
                    >
                        
                        {/* SelectDateCalander */}
                        <SelectDateCalander 
                            onDateSelection={onDateSelection}
                            activeDates={selectedDoctor?.availability.map(da => da.date) || []}
                            selectedDate={selectedDate?.date || null}
                            isLoading={isLoading}
                            language={language}
                            additionalClassName="
                                !px-[23px]
                                !py-[20px]
                                overflow-y-auto
                                border-solid
                                border-[#EDEDED]
                                border-[1px]
                                md:h-full
                                rounded-[16px]
                            "
                        />


                        {/* DoctorAvailableSlots */}
                        <div
                            className={`
                                ${flowSliderStep === 0 
                                    // to left - closing 
                                    ?   'grid-rows-[0fr] md:grid-rows-[1fr] duration-[1000ms] delay-[200ms]' 
                                    // to right - opening
                                    :   'grid-rows-[1fr] md:grid-rows-[1fr] duration-[1000ms] delay-[800ms]'
                                }
                                grid 
                                h-full
                                overflow-hidden
                                transition-[grid-template-rows] 
                                ease-in-out
                            `}
                        > 
                            <div className="overflow-hidden min-h-0">
                                <DoctorAvailableSlots 
                                    isShowing={flowSliderStep === 1 || window.innerWidth > 768}
                                    onTimeSelection={setSelectedTime}
                                    selectedDateSlots={selectedDate || null}
                                    selectedSlot={selectedTime?.start || null}
                                    isLoading={isLoading}
                                    language={language}
                                    country={country}
                                    additionalClassName="
                                        !px-[23px]
                                        !py-[20px]
                                        max-h-auto md:!h-full
                                        overflow-y-auto
                                        border-solid
                                        border-[#EDEDED]
                                        border-[1px]
                                        rounded-[16px]
                                        md:flex-1
                                    "
								    mobileScrollTargetRef={modalContainerRef}
                                    scrollDelay={1100}
                                    scrollOffsetAdjustment={window.innerWidth < 768 ? 190 : 0}
                                />
                            </div>
                        </div>
                    </div>

                    {/* container for confirm appointment button + short notice disclaimer*/}
                    <div 
                        id="reschedule-video-consultation-modal-confirm-appointment-button-and-short-notice-disclaimer-container"
                        className="
                            sticky
                            bottom-0
                            flex
                            flex-col
                            justify-center
                            mt-7
                            bg-white/80
                        "
                    >
                        <Skeletonize
                            isLoading={isLoading}
                            className='h-[48px] md:h-[51px] w-full md:w-3/5 mx-auto'
                            animation='pulse syncWave'
                            rounded='[50px]'
                        >
                            <ButtonV1
                                onClick={handleSubmit}
                                id="reschedule-video-consultation-modal-confirm-appointment-button" 
                                disabled={!allowSubmit}
                                className={`
                                    text-[16px] md:text-[18px]
                                    w-full md:w-fit
                                    md:px-20
                                    !py-3 md:py-2
                                    md:mx-auto
                                    animate-fade-in-up-10
                                `}
                            >
                                {translator.translate(language, "default", "reschedule_video_consultation/confirm_appointment")}
                            </ButtonV1>
                        </Skeletonize>

                        {/* short notice disclaimer if the selected time is less than 24 hours */}
                        <Skeletonize
                            isLoading={isLoading}
                            className='hidden'
                            animation='pulse syncWave'
                        >
                            <div 
                                id="reschedule-video-consultation-modal-short-notice-disclaimer-container"
                                className={`
                                    ${selectedTime && getTimeDifference(selectedTime.start) <= TWENTY_FOUR_HOURS_MS ? 'opacity-100 max-h-[100vh] mt-7 md:mt-9 shadow-[0px_4px_18px_25px_rgba(255,255,255,0.94)]' :  'opacity-0 max-h-0 mt-0'}
                                    transition-all
                                    duration-300
                                    ease-in-out
                                    overflow-hidden
                                    animate-fade-in-up-10
                                `}
                            >
                                <p
                                    id="reschedle-video-consultation-modal-short-notice-disclaimer"
                                    className="
                                        text-[13px] md:text-[15px]
                                        text-[#4B5B4B]
                                        italic
                                        text-center
                                        leading-[1.3]
                                        md:px-12
                                        bg-[rgba(255,255,255,0.94)]
                                        opacity-100
                                    "
                                >
                                    {translator.translate(language, 'choose_doctor', 'appointment_summary/short_notice_disclaimer')}
                                </p>
                            </div>
                        </Skeletonize>
                    </div>
                </div>
            </div>
        </ModalsWrapper>
    )
}

export default RescheduleVideoConsultationModal;


type DoctorsDropdownProps = {
    doctors: DoctorAvailabilityData[];
    selectedDoctor: DoctorAvailabilityData | null;
    onSelectDoctor: (doctor: DoctorAvailabilityData) => void;
    isDoctorsDropdownOpen: boolean;
    setIsDoctorsDropdownOpen: (isOpen: boolean) => void;
    language?: string;
    isLoading?: boolean;
    bottomHeightThreshold: number;
}

const DoctorsDropdown: React.FC<DoctorsDropdownProps> = ({ 
    doctors, 
    selectedDoctor,
    isDoctorsDropdownOpen,
    setIsDoctorsDropdownOpen,
    onSelectDoctor,
    language="de",
    isLoading=false,
    bottomHeightThreshold,
}) => {
    const [isChanging, setIsChanging] = useState<boolean>(false);
    const dropdownTrigerRef = useRef<HTMLDivElement>(null);
    const modalContainerRect = dropdownTrigerRef.current?.getBoundingClientRect();
    const dropDowMmaxHeight = bottomHeightThreshold - (modalContainerRect?.top || window.innerHeight / 2) - (window.innerWidth < 768 ? 110 : 150);

    const handleBlur = (event: React.FocusEvent) => {
        if (isDoctorsDropdownOpen && dropdownTrigerRef.current && !dropdownTrigerRef.current.contains(event.relatedTarget as Node)) {
            setIsDoctorsDropdownOpen(false);
        }
    };


    const handleToggle = () => {
        const newIsOpen = !isDoctorsDropdownOpen;
        setIsDoctorsDropdownOpen(newIsOpen);
        
        // Set focus to the dropdown container when opening
        if (newIsOpen && dropdownTrigerRef.current) {
            dropdownTrigerRef.current.focus();
        }
    };

    const handleDoctorSelect = (doctor: DoctorAvailabilityData) => {
        if (selectedDoctor && doctor.doctorID !== selectedDoctor.doctorID) {
            // Start the magic transition animation
            setIsChanging(true);
            
            // After fade-out completes, update the doctor
            setTimeout(() => {
                onSelectDoctor(doctor);
            }, 200);
            
            // After fade-in completes, reset state and close dropdown
            setTimeout(() => {
                setIsChanging(false);
                setIsDoctorsDropdownOpen(false);
            }, 400);
        } else {
            // Same doctor or no previous selection, just close
            onSelectDoctor(doctor);
            setIsDoctorsDropdownOpen(false);
        }
    };

    return (
        <div 
            onBlur={handleBlur}
            tabIndex={-1}
            ref={dropdownTrigerRef}
            id="reschedule-video-consultation-modal-doctor-drop-down"
            className="
                relative
                z-20
            "
        >
            {/* selected doctor Card - Always visible */}
            <div
                id="reschedule-video-consultation-modal-dropdown-selected-doctor-card"
                onClick={handleToggle}
                    className={`
                    grid
                    items-center
                    border-solid
                    border-[#EDEDED]
                    border-[1px]
                    rounded-[16px]
                    grid-cols-[auto_1fr_auto]
                    py-[10px]
                    px-[23px]
                    mb-3 md:mb-5
                    cursor-pointer
                    transition-all
                    ease-in-out
                    ${isChanging ? 'opacity-20 brightness-125 scale-102 shadow-xl shadow-gray-400/50' : 'opacity-100 brightness-100 scale-100 shadow-none'}
                `}
                style={{
                    transitionDuration: isChanging ? '200ms' : '600ms'
                }}
            >
                <Skeletonize
                    isLoading={isLoading}
                    className='col-start-1 col-end-2 row-start-1 row-end-3 w-[49px] md:w-[59px] h-[49px] md:h-[59px] mr-[16px] overflow-hidden'
                    animation='syncWave pulse'
                    rounded='[100%]'
                >
                    <div
                        id="reschedule-video-consultation-modal-selected-doctor-image-container"
                        className={`
                            w-[49px] md:w-[59px]
                            h-[49px] md:h-[59px]
                            rounded-[100%]
                            overflow-hidden
                            col-start-1
                            col-end-2
                            bg-gray-300
                            row-start-1
                            row-end-3
                            mr-[16px]
                            transition-all
                            ease-in-out
                            animate-fade-in-up-10
                            ${isChanging ? 'shadow-2xl shadow-gray-300/60 ring-2 ring-gray-300/40' : 'shadow-none ring-0'}
                        `}
                        style={{
                            transitionDuration: isChanging ? '200ms' : '600ms'
                        }}
                    >
                        <img 
                            id="reschedule-video-consultation-modal-selected-doctor-image"
                            src={clientConfig.IMAGES_URL + selectedDoctor?.imageSrc} 
                            className={`
                                w-full 
                                h-full 
                                object-cover 
                                transition-all 
                                ease-in-out
                                ${isChanging ? 'brightness-105 contrast-105 grayscale-10' : 'brightness-100 contrast-100 grayscale-0'}
                            `}
                            style={{
                                transitionDuration: isChanging ? '200ms' : '600ms'
                            }}
                        />
                    </div>
                </Skeletonize>

                <Skeletonize
                    isLoading={isLoading}
                    className='col-start-2 col-end-3 row-start-1 row-end-2 h-5 w-[70px] self-start'
                    animation='pulse syncWave'
                >
                    <h3
                        id="reschedule-video-consultation-modal-selected-doctor-name"
                        className={`
                            capitalize
                            justify-self-start
                            self-start
                            text-[#000000]
                            text-[16px] md:text-[18px]
                            font-bold
                            font-roboto
                            leading-[1.5]
                            col-start-2
                            col-end-3
                            row-start-1
                            row-end-2
                            transition-all
                            ease-in-out
                            animate-fade-in-up-10
                            ${!selectedDoctor?.expertise ? 'row-end-3 !self-center' : ''}
                            ${isChanging ? 'transform translate-y-1 text-gray-400' : 'transform translate-y-0 text-[#000000]'}
                        `}
                        style={{
                            transitionDuration: isChanging ? '200ms' : '600ms'
                        }}
                    >
                        {selectedDoctor?.name ?? translator.translate(language, "default", "reschedule_video_consultation/no_doctor_selected")}
                    </h3>
                </Skeletonize>

                <Skeletonize
                    isLoading={isLoading}
                    className='col-start-2 col-end-3 row-start-2 row-end-3 w-[60px] h-4 self-end'
                    skeletonCount={1}
                    animation='pulse syncWave'
                >
                    {selectedDoctor?.expertise && (
                        <h4
                            id="reschedule-video-consultation-modal-selected-doctor-specialty"
                            className={`
                                justify-self-start
                                bg-[#E8F2E6]    
                                self-end
                                capitalize
                                w-fit
                                h-fit
                                rounded-[100px]
                                py-[5px]
                                px-[10px]
                                text-[#000000]
                                text-[12px]
                                font-normal
                                font-roboto
                                leading-[1]
                                col-start-2
                                col-end-3
                                row-start-2
                                row-end-3
                                min-w-0 max-w-full truncate
                                transition-all
                                ease-in-out
                                animate-fade-in-up-10
                                ${isChanging ? 'bg-gray-100 border border-gray-200 text-gray-500' : 'bg-[#E8F2E6] border-0'}
                            `}
                            style={{
                                transitionDuration: isChanging ? '200ms' : '600ms'
                            }}
                        >
                            {selectedDoctor.expertise}
                        </h4>
                    )}
                </Skeletonize>

                <Skeletonize
                    isLoading={isLoading}
                    className='col-start-3 col-end-4 row-start-1 row-end-3 '
                    animation='pulse syncWave'
                >
                    <button
                        id="reschedule-video-consultation-modal-change-doctor-button"
                        disabled={isChanging}
                        className={`
                            w-fit
                            h-fit
                            border-none
                            font-roboto
                            font-medium
                            justify-self-end
                            underline
                            p-0
                            bg-transparent
                            text-[13px] md:text-[16px]
                            col-start-3
                            col-end-4
                            row-start-1
                            row-end-3
                            cursor-pointer
                            hover:text-[#087a6a]
                            transition-all
                            flex
                            items-center
                            gap-1
                            ml-5
                            animate-fade-in-up-10
                            ${isChanging ? 'opacity-30 cursor-not-allowed text-gray-400' : 'opacity-100 cursor-pointer text-[#0A9281]'}
                        `}
                        style={{
                            transitionDuration: isChanging ? '200ms' : '600ms'
                        }}
                    >
                        {isDoctorsDropdownOpen ? (
                            <img 
                                src={ChevronUpIcon} 
                                alt="Close dropdown"
                                className="w-4 h-4"
                                style={{
                                    filter: 'brightness(0)'
                                }}
                            />
                        ) : (
                            translator.translate(language, "default", "reschedule_video_consultation/change_doctor")
                        )}

                    </button>
                </Skeletonize>
            </div>
            

            {/* Dropdown container with grid animation */}
            <div 
                id="reschedule-video-consultation-modal-doctor-dropdown"
                className={`
                    grid
                    transition-all 
                    duration-700 
                    ease-in-out
                    overflow-x-hidden
                    border-solid
                    border-[#EDEDED]
                    absolute
                    top-[calc(100%-1px)]
                    left-0
                    right-0
                    rounded-[16px]
                    bg-[#ffffff]
                    shadow-[0px_25px_25px_0px_#00000043]
                    ${isDoctorsDropdownOpen ? 'border-[1px]' : 'border-[0px]'}
                `}
                style={{
                    gridTemplateRows: isDoctorsDropdownOpen ? '1fr' : '0fr'
                }}
            >
                <div id="reschedule-video-consultation-modal-doctor-dropdown-content" className="overflow-hidden">
                    {/* <div className="min-h-0"> */}
                    <div
                        id="reschedule-video-consultation-modal-doctor-dropdown-content-scrollable-container"
                        className="
                            overflow-y-auto
                            bgg-red-500
                            custom-scrollbar
                            no-margin-top
                        "
                        style={{
                            maxHeight: dropDowMmaxHeight
                        }}
                    >
                        {doctors.map((doctor, index) => {
                            const isSelected = selectedDoctor?.doctorID === doctor.doctorID;
                            
                            return (
                                <div
                                    onClick={() => handleDoctorSelect(doctor)}
                                    key={doctor.doctorID}
                                    className={`
                                        grid
                                        items-center
                                        grid-cols-[auto_1fr_auto]
                                        py-[10px]
                                        px-[23px]
                                        cursor-pointer
                                        transition-all
                                        duration-200
                                        relative
                                        hover:bg-[#f8f9fa]
                                    `}
                                    style={{
                                        borderBottom: index !== doctors.length - 1 ? '1px solid #EDEDED' : 'none'
                                    }}
                                >
                                    <div
                                        id="reschedule-video-consultation-modal-doctor-dropdown-content-doctor-image-container"
                                        className="
                                            w-[49px] md:w-[59px]
                                            h-[49px] md:h-[59px]
                                            rounded-[100%]
                                            overflow-hidden
                                            col-start-1
                                            col-end-2
                                            row-start-1
                                            row-end-3
                                            mr-[16px]
                                            bg-gray-100
                                            border-[#ecececab]
						                    border-solid
						                    border-[1px]
                                        "
                                    >
                                        <img 
                                            src={clientConfig.IMAGES_URL + doctor.imageSrc} 
                                            alt={doctor.name}
                                            className="w-full h-full object-cover text-center text-[10px]"
                                        />
                                    </div>

                                    <h3
                                        id="reschedule-video-consultation-modal-doctor-dropdown-content-doctor-name"
                                        className={`
                                            capitalize
                                            justify-self-start
                                            self-start
                                            text-[#000000]
                                            text-[16px] md:text-[18px]
                                            font-bold
                                            font-roboto
                                            leading-[1.5]
                                            col-start-2
                                            col-end-3
                                            row-start-1
                                            row-end-2
                                            ${!doctor?.expertise ? 'row-end-3 self-center' : ''}
                                        `}
                                    >
                                        {doctor.name}
                                    </h3>

                                    {doctor.expertise &&
                                        <h4
                                            id="reschedule-video-consultation-modal-doctor-dropdown-content-doctor-specialty"
                                            className="
                                                justify-self-start
                                                bg-[#E8F2E6]    
                                                self-end
                                                capitalize
                                                h-fit
                                                rounded-[100px]
                                                py-[5px]
                                                px-[10px]
                                                text-[#000000]
                                                text-[12px]
                                                font-normal
                                                font-roboto
                                                leading-[1]
                                                col-start-2
                                                col-end-3
                                                row-start-2
                                                row-end-3
                                                min-w-0
                                                max-w-[100%]
                                                truncate
                                            "
                                        >
                                            {doctor.expertise}
                                        </h4>
                                    }

                                    <div
                                        id="reschedule-video-consultation-modal-doctor-dropdown-content-doctor-selected-status"
                                        className="
                                            justify-self-end
                                            col-start-3
                                            col-end-4
                                            row-start-1
                                            row-end-3
                                            flex
                                            items-center
                                            ml-2
                                        "
                                    >
                                        {isSelected ? (
                                            <span 
                                                className="
                                                    text-[#A9A9A9]
                                                    font-medium
                                                    text-[13px]
                                                    md:text-[16px]
                                                    italic
                                                "
                                            >
                                                {translator.translate(language, "default", "reschedule_video_consultation/selected")}
                                            </span>
                                        ) : (
                                            <span className="text-[#0A9281] underline font-medium text-[13px] md:text-[16px]">
                                                {translator.translate(language, "default", "reschedule_video_consultation/select")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}


/**
 * Finds the original meeting time slot and its date availability
 * @param oldMeetingDate - Original meeting date in ISO format
 * @param doctorAvailability - Array of DateAvailability objects  
 * @returns Object with timeSlot and dateAvailability, or null if not found
 */
const findOriginalMeetingInfo = (
    oldMeetingDate: string,
    doctorAvailability: DateAvailability[]
  ) => {
    try {
      const originalMeetingDate = new Date(oldMeetingDate);
      const originalDateString = originalMeetingDate.toISOString().split('T')[0];
      
      const matchingDateAvailability = doctorAvailability.find(
        (dateAvail) => dateAvail.date === originalDateString
      );
      
      if (!matchingDateAvailability) {
        return null;
      }
      
      const matchingTimeSlot = matchingDateAvailability.timeslots.find((timeSlot) => {
        const timeslotStartDate = new Date(timeSlot.start);
        return timeslotStartDate.getTime() === originalMeetingDate.getTime();
      });
      
      if (!matchingTimeSlot) {
        return null;
      }
      
      return {
        timeSlot: matchingTimeSlot,
        dateAvailability: matchingDateAvailability
      };
    } catch (error) {
      window.clientLogger.error('Error finding original meeting info:', error);
      return null;
    }
  };